import React, { useState, useRef } from 'react';
import {X, Calculator, RotateCcw, Download, Circle, AlertTriangle} from 'lucide-react';
import html2canvas from 'html2canvas';

interface FlujoCriticoCircularCalculatorProps {
  onClose: () => void;
}

interface Inputs {
  Gasto: string;
  Diametro: string;
}

interface Results {
  TCritico: string;
  Ahid: string;
  Pmojado: string;
  RHid: string;
  EAgua: string;
  Velocidad: string;
  NFroud: string;
  EnergiaE: string;
  Tflujo: string;
}

const emptyInputs: Inputs = {
  Gasto: '',
  Diametro: ''
};

const emptyResults: Results = {
  TCritico: '',
  Ahid: '',
  Pmojado: '',
  RHid: '',
  EAgua: '',
  Velocidad: '',
  NFroud: '',
  EnergiaE: '',
  Tflujo: ''
};

const FlujoCriticoCircularCalculator: React.FC<FlujoCriticoCircularCalculatorProps> = ({ onClose }) => {
  const [inputs, setInputs] = useState<Inputs>(emptyInputs);
  const [results, setResults] = useState<Results>(emptyResults);
  const [error, setError] = useState<string>('');
  const exportRef = useRef<HTMLDivElement>(null);

  const handleChange = (field: keyof Inputs) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputs(prev => ({ ...prev, [field]: e.target.value }));
  };

  const truncate4 = (val: number) => Math.trunc(val * 10000.0) / 10000.0;

  const calcular = () => {
    setError('');

    const q = Number(inputs.Gasto);
    const d = Number(inputs.Diametro);

    if (q <= 0 || d <= 0 || isNaN(q) || isNaN(d)) {
      setError('Datos faltantes o son irreales');
      setResults(emptyResults);
      return;
    }

    // Verificar capacidad máxima a tir = 0.99*d
    let tir = 0.99 * d;
    let angulo = Math.acos(1 - 2 * tir / d);
    let an = (1 / 4) * (angulo - (1 / 2) * Math.sin(2 * angulo)) * Math.pow(d, 2);
    let asl = d * Math.sin(angulo);
    const qc = Math.pow((Math.pow(an, 3) / asl) * 9.81, 0.5);

    if (q > qc) {
      setError('Aumente el diámetro o disminuya el gasto');
      setResults(emptyResults);
      return;
    }

    // Bisección
    let aa = 0;
    let bb = d;
    let res = 1;
    let pn = 0;
    let rh = 0;
    let iter = 0;

    while (Math.abs(res) >= 0.00000011 && iter < 1000) {
      tir = (bb + aa) / 2;
      angulo = Math.acos(1 - 2 * tir / d);
      an = (1 / 4) * (angulo - (1 / 2) * Math.sin(2 * angulo)) * Math.pow(d, 2);
      pn = angulo * d;
      rh = an / pn;
      asl = d * Math.sin(angulo);
      res = Math.pow(an, 3) / asl - Math.pow(q, 2) / 9.81;
      if (res > 0) {
        bb = tir;
      } else {
        aa = tir;
      }
      iter++;
    }

    const v = q / an;
    const fr = v / Math.pow((9.81 * an / asl), 0.5);
    const e = tir + Math.pow(v, 2) / (2 * 9.81);

    setResults({
      TCritico: truncate4(tir).toString(),
      Ahid: truncate4(an).toString(),
      Pmojado: truncate4(pn).toString(),
      RHid: truncate4(rh).toString(),
      EAgua: truncate4(asl).toString(),
      Velocidad: truncate4(v).toString(),
      NFroud: truncate4(fr).toString(),
      EnergiaE: truncate4(e).toString(),
      Tflujo: 'Crítico'
    });
  };

  const limpiar = () => {
    setInputs(emptyInputs);
    setResults(emptyResults);
    setError('');
  };

  const exportarPNG = async () => {
    if (!exportRef.current) return;
    try {
      const canvas = await html2canvas(exportRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true
      });
      const link = document.createElement('a');
      link.download = `flujo-critico-circular-openHH-${new Date().getTime()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Error al exportar PNG:', err);
    }
  };

  const inputFields: { name: keyof Inputs; label: string; symbol: string; unit: string }[] = [
    { name: 'Gasto', label: 'Gasto o Caudal', symbol: 'Q', unit: 'm³/s' },
    { name: 'Diametro', label: 'Diámetro', symbol: 'D', unit: 'm' }
  ];

  const resultFields: { key: keyof Results; label: string; unit: string; highlight?: boolean }[] = [
    { key: 'TCritico', label: 'Tirante Crítico (Yc)', unit: 'm', highlight: true },
    { key: 'Ahid', label: 'Área Hidráulica', unit: 'm²' },
    { key: 'Pmojado', label: 'Perímetro Mojado', unit: 'm' },
    { key: 'RHid', label: 'Radio Hidráulico', unit: 'm' },
    { key: 'EAgua', label: 'Espejo del Agua', unit: 'm' },
    { key: 'Velocidad', label: 'Velocidad', unit: 'm/s' },
    { key: 'NFroud', label: 'Número de Froude', unit: 'Adim.' },
    { key: 'EnergiaE', label: 'Energía Específica', unit: 'm' },
    { key: 'Tflujo', label: 'Tipo de Flujo', unit: '' }
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-8 relative">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/90 hover:bg-gray-100 flex items-center justify-center shadow-md border border-gray-200 transition-colors"
          >
            <X className="w-5 h-5 text-gray-700" />
          </button>

          <div ref={exportRef} className="bg-white rounded-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-600 via-amber-600 to-red-500 p-6 rounded-t-2xl text-white">
              <div className="flex items-center gap-3 mb-2 pr-12">
                <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-orange-100 text-sm font-medium uppercase tracking-wide">Flujo Crítico</p>
                  <h2 className="text-2xl font-bold">Canal Circular</h2>
                </div>
              </div>
              <p className="text-orange-50 text-sm leading-relaxed">
                Ingrese los datos y calcule el tirante crítico, velocidad, número de Froude y demás parámetros hidráulicos para un canal de sección circular.
              </p>
            </div>

            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Datos a ingresar */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b-2 border-amber-400">
                  <Circle className="w-5 h-5 text-amber-600" />
                  <h3 className="font-bold text-amber-600 uppercase tracking-wide text-sm">Datos a ingresar</h3>
                </div>

                <CircularChannelDiagram diametro={inputs.Diametro} tirante={results.TCritico} />

                {inputFields.map(field => (
                  <div key={field.name}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <span className="font-bold text-orange-700">{field.symbol}</span> = {field.label}
                      <span className="text-gray-500 ml-1">({field.unit})</span>
                    </label>
                    <input
                      type="number"
                      value={inputs[field.name]}
                      onChange={handleChange(field.name)}
                      step="any"
                      min="0"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all"
                      placeholder="0.00"
                    />
                  </div>
                ))}

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    ⚠️ {error}
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={calcular}
                    className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white font-semibold py-3 px-5 rounded-lg shadow-md transition-all"
                  >
                    <Calculator className="w-4 h-4" />
                    Calcular
                  </button>
                  <button
                    onClick={limpiar}
                    className="flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-5 rounded-lg transition-all"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Limpiar
                  </button>
                </div>
              </div>

              {/* Resultados */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 pb-2 border-b-2 border-orange-400">
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                  <h3 className="font-bold text-orange-600 uppercase tracking-wide text-sm">Resultados</h3>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-4 space-y-2.5 border border-orange-100">
                  {resultFields.map(field => (
                    <div
                      key={field.key}
                      className={`flex items-center justify-between gap-3 py-2 px-3 rounded-lg ${
                        field.highlight ? 'bg-white shadow-sm border border-orange-200' : ''
                      }`}
                    >
                      <span className={`text-sm ${field.highlight ? 'font-semibold text-gray-800' : 'text-gray-600'}`}>
                        {field.label}
                        {field.unit && <span className="text-xs text-gray-400 ml-1">({field.unit})</span>}
                      </span>
                      <span
                        className={`font-mono text-sm px-3 py-1 rounded ${
                          field.highlight
                            ? 'bg-orange-600 text-white font-bold'
                            : results[field.key]
                            ? 'bg-white text-gray-800 border border-gray-200'
                            : 'bg-gray-100 text-gray-400'
                        }`}
                      >
                        {(results[field.key] as string) || '—'}
                      </span>
                    </div>
                  ))}
                </div>

                {results.Tflujo && (
                  <div className="rounded-lg p-3 text-center font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                    Régimen: {results.Tflujo}
                  </div>
                )}
              </div>
            </div>

            {/* Footer brand */}
            <div className="px-6 pb-4 pt-2 text-center border-t border-gray-100 mt-2">
              <p className="text-xs text-gray-400 font-medium">
                Generado por <span className="text-orange-600 font-bold">openHH</span> · HHTools · {new Date().toLocaleDateString('es-ES')}
              </p>
            </div>
          </div>

          {/* Export button */}
          <div className="px-6 pb-6">
            <button
              onClick={exportarPNG}
              disabled={!results.TCritico}
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-slate-800 to-slate-700 hover:from-slate-900 hover:to-slate-800 disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed text-white font-semibold py-3 px-5 rounded-lg shadow-md transition-all"
            >
              <Download className="w-4 h-4" />
              Exportar resultados como PNG
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CircularChannelDiagram: React.FC<{ diametro: string; tirante: string }> = ({ diametro, tirante }) => {
  const d = Number(diametro);
  const yn = Number(tirante);
  const hasD = isFinite(d) && d > 0;
  const validYn = hasD && isFinite(yn) && yn > 0 ? Math.min(yn, d) : 0;
  const fillRatio = hasD ? validYn / d : 0;
  const fillPct = Math.round(fillRatio * 100);

  const size = 200;
  const cx = size / 2;
  const cy = size / 2 + 8;
  const r = 74;

  const waterLevelY = cy + r - fillRatio * (2 * r);

  const angle = fillRatio > 0 ? Math.acos(1 - 2 * fillRatio) : 0;
  const halfChord = r * Math.sin(angle);
  const waterLeftX = cx - halfChord;
  const waterRightX = cx + halfChord;

  const largeArcFlag = fillRatio > 0.5 ? 1 : 0;

  const waterPath =
    fillRatio <= 0
      ? ''
      : fillRatio >= 1
      ? `M ${cx - r} ${cy} A ${r} ${r} 0 1 1 ${cx + r} ${cy} A ${r} ${r} 0 1 1 ${cx - r} ${cy} Z`
      : `M ${waterLeftX} ${waterLevelY} L ${waterRightX} ${waterLevelY} A ${r} ${r} 0 ${largeArcFlag} 1 ${waterLeftX} ${waterLevelY} Z`;

  let estado = 'Sin datos';
  if (hasD) {
    if (fillRatio <= 0) estado = 'Vacío';
    else if (fillRatio >= 0.999) estado = 'Lleno';
    else if (fillRatio < 0.5) estado = 'Menor a la mitad';
    else if (fillRatio > 0.5) estado = 'Mayor a la mitad';
    else estado = 'A la mitad';
  }

  return (
    <div className="bg-white rounded-lg border border-amber-200 p-3 flex flex-col items-center">
      <svg width={size} height={size + 14} viewBox={`0 0 ${size} ${size + 14}`}>
        <defs>
          <clipPath id="fcc-pipe-clip">
            <circle cx={cx} cy={cy} r={r - 3} />
          </clipPath>
        </defs>

        {/* Tubo (pared) */}
        <circle cx={cx} cy={cy} r={r} fill="#f8fafc" stroke="#94a3b8" strokeWidth={4} />
        <circle cx={cx} cy={cy} r={r - 3} fill="#eef2f7" />

        {/* Agua */}
        {fillRatio > 0 && (
            <path d={waterPath} fill="#38bdf8" opacity={0.75} clipPath="url(#fcc-pipe-clip)" />
          )}

        {/* Línea de nivel de agua */}
        {fillRatio > 0 && fillRatio < 1 && (
          <line
            x1={cx - r - 6}
            y1={waterLevelY}
            x2={cx + r + 6}
            y2={waterLevelY}
            stroke="#0284c7"
            strokeWidth={1}
            strokeDasharray="3,3"
          />
        )}

        {/* Cota de diámetro */}
        <line x1={cx - r} y1={cy + r + 14} x2={cx + r} y2={cy + r + 14} stroke="#f59e0b" strokeWidth={1.5} markerStart="url(#fcc-arrow)" markerEnd="url(#fcc-arrow)" />
        <text x={cx} y={cy + r + 28} textAnchor="middle" fontSize="11" fill="#b45309" fontWeight="600">
          D = {hasD ? d.toFixed(2) : '—'} m
        </text>

        <defs>
          <marker id="fcc-arrow" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6" fill="none" stroke="#f59e0b" strokeWidth="1" />
          </marker>
        </defs>

        {/* Etiqueta Yc */}
        <text x={cx + r + 14} y={waterLevelY > cy - r ? waterLevelY + 4 : cy - r + 4} fontSize="10" fill="#0284c7" fontWeight="600">
          Yc = {validYn > 0 ? validYn.toFixed(2) : '—'} m
        </text>
      </svg>

      <div className="text-center mt-1">
        <span className="text-xs font-semibold text-amber-700">
          {hasD ? `${fillPct}% de llenado` : 'Ingrese el diámetro'}
        </span>
        <span className="block text-[11px] text-gray-500">{estado}</span>
      </div>
    </div>
  );
};

export default FlujoCriticoCircularCalculator;
